import { HowTo } from '../types';

/**
 * Weekly Schedule How Tos.
 *
 * Copy rules (docs/Help-System-POC-Plan.md §14): action first, reason after;
 * UI labels in bold exactly as the screen spells them; none of the code's
 * vocabulary — "this date only", never "one-off scope exception".
 */
export const scheduleHowTos: HowTo[] = [
  {
    key: 'schedule.change-session-times',
    version: 2,
    section: 'Weekly Schedule',
    title: "Change the time of a child's session this week",
    summary:
      'Adjust when a session starts or ends — for this date only, or from this date onwards.',
    youNeed: [
      "A session already in this week's schedule",
      'No attendance recorded against it yet — see step 7',
    ],
    keywords: [
      'session', 'sessions', 'time', 'times', 'timing', 'change', 'edit', 'move',
      'adjust', 'start', 'end', 'finish', 'earlier', 'later', 'longer', 'shorter',
      'hours', 'wrong time', 'resize', 'drag', 'schedule', 'timetable', 'week',
      'lock', 'locked', 'unlock', 'padlock', 'frozen',
      'revert', 'undo', 'usual time', 'put back', 'pen',
    ],
    routes: ['/schedule'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Weekly Schedule** from the menu. If the menu is a strip of icons, hover over it or click the ☰ button.',
        target: 'nav.schedule',
        route: '/schedule',
        navigateOnly: true,
      },
      {
        text: 'Use the arrows to move to the week you want. The rail on the left filters the grid to one child.',
        target: 'period-nav',
      },
      {
        text: "Click the padlock to unlock the grid. It's locked by default so stray clicks can't create sessions.",
        target: 'schedule.grid-lock',
      },
      {
        text: "Click the session you want to change. Its details open on the right. Cancelled sessions can't be resized — restore the date first.",
        target: 'schedule.grid',
      },
      {
        text: "Drag the left edge of the bar to change the start, the right edge for the end. Dragging moves in fixed steps — for a time between them, type it into **Start** and **End**, then **Save changes**. To shift the whole session and keep its length, drag the middle of the bar instead.",
        target: 'schedule.session-selected',
        seeAlso: { guideKey: 'schedule.move-session', label: 'Show me moving a session' },
      },
      {
        text: "If the session repeats, you're asked how far the change reaches: **Just this date**, or **This date and future**, which leaves earlier weeks alone. **Cancel** undoes it. A bar changed for one date only shows a small pen — hover its initials to see its usual times. To undo it, click the session and use **Revert to the usual time** in the blue box at the top.",
      },
      {
        text: "A message about attendance means a record already exists against the session. Clear it on the **Attendance** page first. **This date and future** checks later dates too, so it can be blocked when **Just this date** isn't.",
        seeAlso: { guideKey: 'attendance.remove-record', label: 'Show me how to remove it' },
      },
      {
        text: 'Done. Click the padlock again to re-lock the grid.',
        target: 'schedule.grid-lock',
      },
    ],
    related: ['schedule.move-session', 'attendance.remove-record'],
  },
  {
    key: 'schedule.move-session',
    version: 2,
    section: 'Weekly Schedule',
    title: 'Move a session to another day or time',
    summary:
      'Drag a session to a new day or time — for this date only, or from this date onwards.',
    youNeed: [
      "A session already in this week's schedule",
      'No attendance recorded against it yet — see step 7',
    ],
    keywords: [
      'move', 'moving', 'drag', 'day', 'different day', 'another day', 'swap',
      'switch', 'reschedule', 'change day', 'session', 'sessions', 'schedule',
      'timetable', 'week', 'this week only', 'lock', 'locked', 'unlock',
    ],
    routes: ['/schedule'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Weekly Schedule** from the menu. If the menu is a strip of icons, hover over it or click the ☰ button.',
        target: 'nav.schedule',
        route: '/schedule',
        navigateOnly: true,
      },
      {
        text: 'Use the arrows to move to the week you want to change.',
        target: 'period-nav',
      },
      {
        text: "Click the padlock to unlock the grid. It's locked by default so stray clicks can't move sessions.",
        target: 'schedule.grid-lock',
      },
      {
        // Up/down is the day, sideways is the time; a grey bar means it would
        // double-book the child and won't be dropped there.
        text: "Press on the middle of the session and drag it: up or down to another day, left or right to another time. It keeps its length. If it turns grey, that child already has a session there — it can't go there. Let go outside the grid to call the move off. A cancelled session won't move — restore the date first.",
        target: 'schedule.grid',
      },
      {
        text: "If the session repeats, you're asked how far the move reaches: **Just this date** moves this week's session only. **This date and future** moves every week from now on and leaves earlier weeks alone.",
      },
      {
        // Day move: the source date is cancelled with reason "Moved to …".
        // Same-day move: an override, marked by the pen (title on the label).
        text: "If you chose **Just this date**: a move to another day leaves the old day faded and struck through, like a cancelled session — click it to see where it moved to. A move on the same day leaves nothing behind; instead the bar shows a small pen. Hover its initials to see its usual times.",
        target: 'schedule.grid',
      },
      {
        text: 'A message about attendance means a record already exists against the session. Clear it on the **Attendance** page first. **This date and future** checks later dates too, so it can be blocked when **Just this date** isn\'t.',
        seeAlso: { guideKey: 'attendance.remove-record', label: 'Show me how to remove it' },
      },
      {
        text: 'Done. Click the padlock again to re-lock the grid.',
        target: 'schedule.grid-lock',
      },
    ],
    related: ['schedule.change-session-times', 'attendance.remove-record'],
  },
  {
    key: 'schedule.cancel-one-date',
    version: 2,
    section: 'Weekly Schedule',
    title: 'Cancel a session for one date',
    summary:
      'Take a session out of one day — a closure, a trip, a child away — without touching the rest of the term. And put it back.',
    youNeed: [
      'The session in the week on screen',
      'No attendance recorded against it on that date — see step 6',
    ],
    keywords: [
      'cancel', 'cancelled', 'cancellation', 'remove', 'delete', 'skip',
      'not running', 'closed', 'closure', 'trip', 'inset', 'bank holiday',
      'one off', 'one-off', 'just this date', 'restore', 'put back', 'undo',
      'session', 'sessions', 'schedule', 'timetable', 'away', 'absent',
    ],
    routes: ['/schedule'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Weekly Schedule** from the menu and move to the week holding the date.',
        target: 'nav.schedule',
        route: '/schedule',
      },
      {
        text: 'Click the padlock to unlock the grid.',
        target: 'schedule.grid-lock',
      },
      {
        text: 'Click the session on the date you want to cancel. Its details open on the right.',
        target: 'schedule.grid',
      },
      {
        text: 'Click **Cancel for this date**. Only that date is affected — every other week keeps the session.',
        target: 'schedule.cancel-date',
      },
      {
        text: 'Give a reason: pick one from the list, or choose **Other** and type it. The reason is what anyone looking at that week later will see.',
      },
      {
        text: "If you see a message about attendance, the date can't be cancelled — a record already exists for it. Delete that record on the **Attendance** page first, then come back.",
        seeAlso: { guideKey: 'attendance.remove-record', label: 'Show me how to remove it' },
      },
      {
        text: 'The session stays on the grid, struck through and faded, so the week still shows what was meant to happen. Its resize handles are gone while it is cancelled.',
        target: 'schedule.grid',
      },
      {
        text: 'Changed your mind? Click the session again and use **Restore session for this date**. It comes back exactly as it was.',
        target: 'schedule.restore-date',
      },
      {
        // moveOneDateToAnotherDay: the cancellation and the one-off aren't
        // linked, so restoring one leaves the other in place.
        text: 'A date marked **Moved to …** was moved to another day, not cancelled. Restoring it does not take the session off the new day — cancel that one too, or the child has it twice that week.',
        target: 'schedule.grid',
      },
    ],
    related: ['schedule.change-session-times', 'schedule.move-session', 'attendance.remove-record'],
  },
  {
    key: 'schedule.add-session',
    version: 1,
    section: 'Weekly Schedule',
    title: 'Add a session to the schedule',
    summary:
      'Put a new session on the grid — for one child, repeating each week from that date.',
    youNeed: ['A child to schedule it for'],
    keywords: [
      'add', 'new', 'create', 'session', 'sessions', 'schedule', 'timetable',
      'slot', 'book', 'booking', 'place', 'draw', 'drag', 'repeat', 'weekly',
      'recurring', 'every week',
    ],
    routes: ['/schedule'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Weekly Schedule** from the menu and move to the week the session starts in.',
        target: ['period-nav', 'nav.schedule'],
        route: '/schedule',
      },
      {
        text: 'Click the padlock to unlock the grid. Nothing can be created while it is locked — that is what stops a stray tap booking a session.',
        target: 'schedule.grid-lock',
      },
      {
        text: 'Click an empty slot on the day and time you want, and the session appears at the default length. Drag across the slots instead to draw it at the length you want.',
        target: 'schedule.grid',
      },
      {
        text: 'Check the child at the top of the panel. A new session takes it from the rail filter, or from the last session you saved — click it to set or change it.',
        target: 'schedule.child-select',
      },
      {
        text: 'Set the times, and the staff for the session.',
      },
      {
        text: 'A brand-new session saves itself as you go. Once you close the panel it settles, and later changes need **Save changes**.',
      },
      {
        text: 'The session now repeats every week from that date. To take out a single date later, cancel that date rather than deleting the session.',
        seeAlso: { guideKey: 'schedule.cancel-one-date', label: 'Show me how to cancel one date' },
      },
      {
        text: 'Lock the grid again when you have finished.',
        target: 'schedule.grid-lock',
      },
    ],
    related: ['schedule.session-defaults', 'schedule.change-session-times'],
  },
  {
    key: 'schedule.session-defaults',
    version: 1,
    section: 'Weekly Schedule',
    title: 'Setting defaults for new sessions',
    summary:
      'Choose the child, staff and length a freshly created session begins with, so a run of them takes fewer clicks each.',
    youNeed: ['The Weekly Schedule, with the grid unlocked'],
    keywords: [
      'default', 'defaults', 'new session', 'length', 'duration', 'hour',
      'minutes', 'staff', 'teacher', 'teachers', 'pin', 'pinned', 'child',
      'prefill', 'pre-fill', 'quicker', 'repeat', 'same again',
    ],
    routes: ['/schedule'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Weekly Schedule** from the menu.',
        target: 'nav.schedule',
        route: '/schedule',
        navigateOnly: true,
      },
      {
        // Two labels, one control: "Session defaults" until a default child is
        // set, then "Session defaults:" followed by what it will use.
        // Anyone following this guide is in the first state.
        text: 'Under the heading, click the **Session defaults** pill. Once set, it shows what the next session you create will start with.',
        target: 'schedule.defaults-pill',
      },
      // Child, staff, length, location — the order the modal itself puts them
      // in, so the reader works down the panel rather than around it.
      {
        text: 'Choose the **CHILD**. If a child is already selected from the left filter rail, that is the one a new session uses anyway.',
        target: 'schedule.child-select',
      },
      {
        text: 'Pin the **STAFF** if a run of sessions shares them. Left alone, each new session inherits the staff from the last one you saved.',
      },
      {
        text: 'Set the **SESSION LENGTH** a click creates — one hour unless you change it. A drag ignores it, since the drag sets the length itself.',
      },
      {
        text: 'Close the pill. The next session you create starts with those, and **Reset to automatic** hands the staff back to following your last save.',
      },
    ],
    related: ['schedule.add-session'],
  },
  {
    key: 'schedule.end-session',
    version: 1,
    section: 'Weekly Schedule',
    title: 'Stop a session happening from now on',
    summary:
      'A child has stopped coming on that day — end the session from a date forwards, without disturbing the weeks already recorded.',
    youNeed: ['The Weekly Schedule, with the grid unlocked'],
    keywords: [
      'stop', 'end', 'ended', 'ending', 'finish', 'finished', 'no longer',
      'not coming', 'stopped coming', 'left', 'leaving', 'dropped',
      'delete', 'remove', 'cancel', 'permanent', 'permanently', 'forever',
      'every week', 'recurring', 'repeat', 'rule', 'from now on', 'future',
      'session', 'sessions', 'schedule',
    ],
    routes: ['/schedule'],
    audience: 'staff',
    steps: [
      {
        text: 'Open **Weekly Schedule** from the menu and move to the first week the session should not appear in. That date is the one it ends from.',
        target: ['period-nav', 'nav.schedule'],
        route: '/schedule',
      },
      {
        text: 'Unlock the grid with the padlock, then click the session.',
        target: 'schedule.grid-lock',
      },
      {
        text: 'At the foot of the panel is a bar headed **This date**. Everything above it edits the weekly session; everything in it acts on that date alone.',
        target: 'schedule.date-bar',
      },
      {
        text: 'Click **End the rule from this date**. This date and every week after it stop appearing; past weeks stay untouched.',
        target: 'schedule.end-rule',
      },
      {
        text: 'Confirm. **Cancel for this date** takes out one occurrence and can be restored; ending the rule cannot be undone — you would add the session back.',
        seeAlso: { guideKey: 'schedule.cancel-one-date', label: 'I only meant one date' },
      },
      {
        text: 'If attendance has been recorded on this date or any later one, ending the rule is blocked until those records are cleared. It says which dates.',
        seeAlso: { guideKey: 'attendance.remove-record', label: 'Show me how to clear a record' },
      },
      {
        text: 'If this date has already had its times changed, the option is not offered at all. Move to a week that has not been changed and end it from there.',
      },
      {
        text: 'If the child has left altogether, an Admin marking them as left is tidier — it clears every list at once.',
        seeAlso: { guideKey: 'admin.mark-as-left', label: 'Show me marking a child as left' },
      },
    ],
    related: ['schedule.cancel-one-date', 'schedule.add-session'],
  },
];
